// src/pages/DebtStudio/reports/buildDebtPortfolioReportData.js

import dayjs from "lib/dayjs";
import { buildPortfolioDebtService } from "pages/DebtStudio/lib/buildPortfolioDebtService";
import { buildPortfolioDetails } from "pages/DebtStudio/lib/buildPortfolioDetails";
import {
  fmtCurrency,
  fmtDate,
  fmtNumber,
  fmtPct,
} from "reporting/pdf/pdfFormatters";
import { PDF_CHART_SERIES } from "reporting/pdf/pdfTheme";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round2(value) {
  return Math.round((num(value) + Number.EPSILON) * 100) / 100;
}

function fmtCurrency2(value) {
  return fmtCurrency(value, 2);
}

function fmtPctFromDecimal(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return fmtPct(n * 100, digits);
}

function fmtYears(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${fmtNumber(n, 2)} yrs`;
}

function getDeal(member) {
  return member?.ds_deals || member?.ds_deals_row || member?.deal || null;
}

function getStructureRows(deal) {
  const raw = deal?.structure;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function principalOf(row) {
  return (
    num(row?.principal) ||
    num(row?.par_amount) ||
    num(row?.parAmount) ||
    num(row?.amount) ||
    0
  );
}

function couponOf(row) {
  const n = Number(row?.coupon);
  return Number.isFinite(n) ? n : null;
}

function yearFracAct365(fromDate, toDate) {
  const a = dayjs(fromDate);
  const b = dayjs(toDate);
  if (!a.isValid() || !b.isValid()) return null;
  return b.diff(a, "day") / 365;
}

function computeWalYearsFromIssues(issues, asOfDate) {
  const asOf = dayjs(asOfDate);
  if (!asOf.isValid()) return null;

  let sumP = 0;
  let sumPT = 0;

  for (const issue of issues || []) {
    for (const row of issue?.couponSchedule || []) {
      const d = dayjs(row?.date);
      if (!d.isValid() || !d.isAfter(asOf, "day")) continue;

      const p = num(row?.principal);
      if (p <= 0) continue;

      const t = yearFracAct365(asOf, d);
      if (t == null) continue;

      sumP += p;
      sumPT += p * t;
    }
  }

  return sumP > 0 ? sumPT / sumP : null;
}

function computeWacPctFromMembers(members) {
  let sumPar = 0;
  let sumParCoupon = 0;

  for (const member of members || []) {
    if (member?.include === false) continue;

    const deal = getDeal(member);
    for (const row of getStructureRows(deal)) {
      const principal = principalOf(row);
      const coupon = couponOf(row);
      if (principal <= 0 || coupon == null || coupon <= 0) continue;

      sumPar += principal;
      sumParCoupon += principal * coupon;
    }
  }

  return sumPar > 0 ? sumParCoupon / sumPar : null;
}

function issueLabel(issue) {
  return issue?.label || issue?.deal?.name || issue?.deal?.deal_name || "Issue";
}

function buildIssueTotals(issues = []) {
  return issues.map((issue) => {
    const totals = (issue.annualTotals || []).reduce(
      (acc, row) => {
        acc.principal += num(row.principal);
        acc.interest += num(row.interest);
        acc.total += num(row.total);
        return acc;
      },
      { principal: 0, interest: 0, total: 0 }
    );

    return {
      issueId: issue.issueId,
      label: issueLabel(issue),
      principal: round2(totals.principal),
      interest: round2(totals.interest),
      total: round2(totals.total),
    };
  });
}

function buildIssueSeries(issues = []) {
  return issues.map((issue, index) => ({
    key: `issue_${issue.issueId}`,
    label: issueLabel(issue),
    color: PDF_CHART_SERIES[index % PDF_CHART_SERIES.length],
  }));
}

function columnsForMoneyRows(labelName = "Category") {
  return [
    { key: "label", label: labelName, align: "left" },
    {
      key: "principal",
      label: "Principal",
      align: "right",
      format: fmtCurrency2,
    },
    {
      key: "interest",
      label: "Interest",
      align: "right",
      format: fmtCurrency2,
    },
    { key: "total", label: "Total", align: "right", format: fmtCurrency2 },
  ];
}

export function buildDebtPortfolioReportData({
  portfolio = {},
  members = [],
  orgName = "",
  periodEndDate = "",
  preparedDate = new Date(),
} = {}) {
  const includedMembers = (Array.isArray(members) ? members : []).filter(
    (member) => member?.include !== false
  );
  const effectivePeriodEnd = periodEndDate || portfolio?.period_end || "";

  const debtService = buildPortfolioDebtService({
    members: includedMembers,
    periodEndOverride: effectivePeriodEnd || null,
  });

  const details = buildPortfolioDetails({
    members: includedMembers,
    fiscalYearEndOverride: effectivePeriodEnd || null,
  });

  const issues = debtService.issues || [];
  const aggregateRows = debtService.aggregateFY || [];
  const issueSeries = buildIssueSeries(issues);
  const issueTotals = buildIssueTotals(issues);
  const asOf = dayjs(preparedDate).startOf("day").format("YYYY-MM-DD");

  const totalPrincipal = aggregateRows.reduce(
    (sum, row) => sum + num(row.principal),
    0
  );
  const totalInterest = aggregateRows.reduce(
    (sum, row) => sum + num(row.interest),
    0
  );
  const totalDebtService = aggregateRows.reduce(
    (sum, row) => sum + num(row.total),
    0
  );
  const avgAnnualDebtService = aggregateRows.length
    ? totalDebtService / aggregateRows.length
    : null;

  const walYears = computeWalYearsFromIssues(details.issues || [], asOf);
  const wacPct = computeWacPctFromMembers(includedMembers);
  const callableCurrent = details.callableVsNoncallableOutstandingFY?.[0] || null;
  const callablePct =
    callableCurrent && num(callableCurrent.total) > 0
      ? num(callableCurrent.callable) / num(callableCurrent.total)
      : null;

  return {
    meta: {
      reportTitle: "DebtStudio - Portfolio Report",
      orgName,
      portfolioName: portfolio?.name || "Portfolio",
      description: portfolio?.description || "",
      periodEndDate: effectivePeriodEnd,
      asOfLabel: `Prepared ${dayjs(preparedDate).format("M/D/YYYY")}`,
      fileDate: dayjs(preparedDate).format("YYYY-MM-DD"),
      issueCount: issues.length,
    },

    kpis: [
      { label: "Issues", value: fmtNumber(issues.length, 0) },
      { label: "Total Principal", value: fmtCurrency2(totalPrincipal) },
      { label: "Total Interest", value: fmtCurrency2(totalInterest) },
      { label: "Total Debt Service", value: fmtCurrency2(totalDebtService) },
      {
        label: "Avg Annual Debt Service",
        value:
          avgAnnualDebtService == null ? "—" : fmtCurrency2(avgAnnualDebtService),
      },
      { label: "WAL", value: fmtYears(walYears) },
      {
        label: "Weighted Avg Coupon",
        value: wacPct == null ? "—" : fmtPct(wacPct, 3),
      },
      {
        label: "Callable %",
        value: callablePct == null ? "—" : fmtPctFromDecimal(callablePct, 1),
      },
    ],

    aggregateDebtService: {
      chartRows: aggregateRows,
      chartSeries: [
        { key: "principal", label: "Principal", color: PDF_CHART_SERIES[0] },
        { key: "interest", label: "Interest", color: PDF_CHART_SERIES[1] },
      ],
      table: {
        title: "Aggregate Debt Service",
        columns: [
          { key: "date", label: "Period End", align: "left", format: fmtDate },
          {
            key: "principal",
            label: "Principal",
            align: "right",
            format: fmtCurrency2,
          },
          {
            key: "interest",
            label: "Interest",
            align: "right",
            format: fmtCurrency2,
          },
          { key: "total", label: "Total", align: "right", format: fmtCurrency2 },
        ],
        rows: aggregateRows,
      },
    },

    byIssueDebtService: {
      chartRows: debtService.stackedByIssueFY || [],
      chartSeries: issueSeries,
      table: {
        title: "Debt Service by Issue Detail",
        columns: columnsForMoneyRows("Issue"),
        rows: issueTotals,
      },
    },

    principalAllocation: {
      chartRows: details.principalDueByPriorityFY || [],
      chartSeries: [
        { key: "senior", label: "Senior", color: PDF_CHART_SERIES[0] },
        { key: "subordinate", label: "Subordinate", color: PDF_CHART_SERIES[1] },
      ],
      table: {
        title: "Principal Allocation",
        columns: [
          { key: "date", label: "Period End", align: "left", format: fmtDate },
          { key: "senior", label: "Senior", align: "right", format: fmtCurrency2 },
          {
            key: "subordinate",
            label: "Subordinate",
            align: "right",
            format: fmtCurrency2,
          },
          { key: "total", label: "Total", align: "right", format: fmtCurrency2 },
        ],
        rows: details.principalDueByPriorityFY || [],
      },
    },

    callable: {
      chartRows: details.callableVsNoncallableOutstandingFY || [],
      chartSeries: [
        { key: "noncallable", label: "Non-callable", color: PDF_CHART_SERIES[1] },
        { key: "callable", label: "Callable", color: PDF_CHART_SERIES[2] },
      ],
      table: {
        title: "Callable / Non-callable Outstanding",
        columns: [
          { key: "date", label: "Period End", align: "left", format: fmtDate },
          {
            key: "noncallable",
            label: "Non-callable",
            align: "right",
            format: fmtCurrency2,
          },
          {
            key: "callable",
            label: "Callable",
            align: "right",
            format: fmtCurrency2,
          },
          { key: "total", label: "Total", align: "right", format: fmtCurrency2 },
        ],
        rows: details.callableVsNoncallableOutstandingFY || [],
      },
    },
  };
}

export default buildDebtPortfolioReportData;
